import { useEffect, useMemo, useState } from 'react'

import { useLazyFetchObjectByCodeQuery } from '@redux/api'

import { getPkField } from '@helpers'
import { GETObjectModel, MapPointGroup, ObjectFieldDTO } from '@types'

import type { ObjectModelInfo } from './types'

/**
 * Хук для загрузки моделей объектов из pointGroups
 */
export const useMapObjectModels = (pointGroups: MapPointGroup[]) => {
  const [fetchObjectModel] = useLazyFetchObjectByCodeQuery()
  const [objectModelsMap, setObjectModelsMap] = useState<Map<string, ObjectModelInfo>>(new Map())
  const [isLoading, setIsLoading] = useState(true)
  const [isReady, setIsReady] = useState(false)

  // Уникальные objectCode (сортируем для стабильности)
  const uniqueObjectCodes = useMemo(
    () => [...new Set(pointGroups.map(group => group.objectCode))].sort(),
    [pointGroups]
  )

  // Строковый ключ для зависимостей эффектов (стабильное представление набора кодов)
  const codesKey = useMemo(() => uniqueObjectCodes.join(','), [uniqueObjectCodes])

  useEffect(() => {
    if (uniqueObjectCodes.length === 0) {
      setObjectModelsMap(new Map())
      setIsLoading(false)
      setIsReady(false)

      return
    }

    let mounted = true
    setIsLoading(true)

    const loadModels = async () => {
      try {
        const modelPromises = uniqueObjectCodes.map(async code => {
          try {
            const model = (await fetchObjectModel(code).unwrap()) as GETObjectModel

            return { code, model }
          } catch (error) {
            return { code, model: null as GETObjectModel | null }
          }
        })

        const results = await Promise.all(modelPromises)

        if (!mounted) return

        const map = new Map<string, ObjectModelInfo>()

        results.forEach(({ code, model }) => {
          if (!model) return

          const pkField = getPkField(model.fields) as ObjectFieldDTO | undefined

          if (pkField && 'name' in pkField) {
            map.set(code, {
              model,
              pkField,
              pkFieldName: pkField.name,
            })
          } else {
            console.error(`[MapWS] PK field not found for object ${code}`)
          }
        })

        setObjectModelsMap(map)
        setIsReady(map.size === uniqueObjectCodes.length)
        setIsLoading(false)
      } catch (error) {
        if (mounted) {
          setIsLoading(false)
          setIsReady(false)
        }
      }
    }

    loadModels()

    return () => {
      mounted = false
    }
  }, [fetchObjectModel, codesKey])

  return { objectModelsMap, isLoading, isReady, uniqueObjectCodes }
}
